export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "name": "Ada Aesthetics",
  "description": "Professional aesthetic treatments including lip fillers, dermal fillers, anti-wrinkle injections, and beauty services in Cheshunt, Hertfordshire.",
  "url": "https://adaaesthetics.uk",
  "telephone": "07478705252",
  "email": "info@adaaesthetics.uk",
  "image": "https://adaaesthetics.uk/images/ada-aesthetics-clinic.jpg",
  "logo": "https://adaaesthetics.uk/images/ada-aesthetics-logo.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "15 Ousden Drive",
    "addressLocality": "Cheshunt",
    "addressRegion": "Hertfordshire",
    "postalCode": "EN8 9RL",
    "addressCountry": "GB"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 51.7024,
    "longitude": -0.0347
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "09:00",
      "closes": "16:00"
    }
  ],
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": 51.7024,
      "longitude": -0.0347
    },
    "geoRadius": "20000"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Aesthetic Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Lip Fillers",
          "description": "Natural-looking lip enhancement using hyaluronic acid fillers",
          "provider": {
            "@type": "MedicalBusiness",
            "name": "Ada Aesthetics"
          }
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Anti-Wrinkle Injections",
          "description": "Botulinum toxin treatments to reduce facial wrinkles",
          "provider": {
            "@type": "MedicalBusiness",
            "name": "Ada Aesthetics"
          }
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service", 
          "name": "Dermal Fillers",
          "description": "Facial volume restoration and contouring treatments",
          "provider": {
            "@type": "MedicalBusiness",
            "name": "Ada Aesthetics"
          }
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Russian Lips",
          "description": "Specialized lip filler technique for natural-looking volume",
          "provider": {
            "@type": "MedicalBusiness",
            "name": "Ada Aesthetics"
          }
        }
      }
    ]
  },
  "priceRange": "££",
  "paymentAccepted": "Cash, Credit Card, Bank Transfer",
  "currenciesAccepted": "GBP"
})

export const generateServiceSchema = (serviceName: string, description: string, price?: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": serviceName,
  "description": description,
  "provider": {
    "@type": "MedicalBusiness",
    "name": "Ada Aesthetics",
    "telephone": "07478705252",
    "address": {
      "@type": "PostalAddress", 
      "streetAddress": "15 Ousden Drive",
      "addressLocality": "Cheshunt",
      "addressRegion": "Hertfordshire",
      "postalCode": "EN8 9RL",
      "addressCountry": "GB"
    }
  },
  "areaServed": {
    "@type": "City",
    "name": "Cheshunt",
    "containedInPlace": {
      "@type": "State",
      "name": "Hertfordshire"
    }
  },
  "serviceType": "Medical Aesthetics",
  "category": "Beauty Treatment",
  ...(price && { 
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    }
  })
})

export const generateBreadcrumbSchema = (breadcrumbs: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": crumb.url
  }))
})

export const generateFAQSchema = (faqs: Array<{question: string, answer: string}>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
})

/**
 * Generates aggregate rating schema for reviews
 * @param ratingValue - Average rating (default: "5.0")
 * @param reviewCount - Number of reviews (default: "500")
 * @param bestRating - Maximum rating (default: "5")
 * @returns Aggregate rating schema object
 */
export const generateAggregateRatingSchema = (
  ratingValue: string = "5.0",
  reviewCount: string = "500",
  bestRating: string = "5"
) => ({
  "@type": "AggregateRating",
  "ratingValue": ratingValue,
  "reviewCount": reviewCount,
  "bestRating": bestRating,
  "worstRating": "1"
})

/**
 * Generates LocalBusiness schema with aggregate rating
 * @returns Enhanced local business schema with reviews
 */
export const generateLocalBusinessSchemaWithReviews = () => {
  const baseSchema = generateLocalBusinessSchema()
  return {
    ...baseSchema,
    "aggregateRating": generateAggregateRatingSchema()
  }
}

/**
 * Combines multiple schema objects into @graph notation
 * @param schemas - Array of schema objects to combine
 * @returns Combined schema markup with @graph array
 */
export const combineSchemas = (...schemas: any[]) => ({
  "@context": "https://schema.org",
  "@graph": schemas
})